import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  ShieldCheck, Phone, MapPin, Loader2, Check, Siren, MessageSquareWarning, Lock,
} from "lucide-react";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-cobalt";

// Stable module-scope components — never redefined per render, so inputs keep focus.
const YesNo = ({ k, label, testid, safety, setSafety }) => (
  <div className="flex items-center justify-between gap-3 py-2.5 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-700">{label}</span>
    <div className="flex gap-1.5 shrink-0">
      {[["Yes", true], ["No", false]].map(([t, v]) => (
        <button key={t} onClick={() => setSafety((s) => ({ ...s, [k]: v }))} data-testid={`${testid}-${t.toLowerCase()}`}
          className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all ${safety[k] === v ? "bg-navy text-white border-navy" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>{t}</button>
      ))}
    </div>
  </div>
);

const Section = ({ done, locked, icon: Icon, title, children }) => (
  <div className={`bg-white rounded-3xl border p-6 ${done ? "border-emerald-100" : locked ? "border-slate-100 opacity-50" : "border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"}`}>
    <div className="flex items-center gap-3">
      <div className={`h-10 w-10 rounded-2xl flex items-center justify-center ${done ? "bg-emerald-50" : "bg-cobalt-light"}`}>
        {done ? <Check className="w-5 h-5 text-emerald-600" /> : locked ? <Lock className="w-5 h-5 text-slate-400" /> : <Icon className="w-5 h-5 text-cobalt" />}
      </div>
      <h3 className="font-heading font-semibold text-navy">{title}</h3>
      {done && <span className="ml-auto text-xs font-semibold text-emerald-600">Complete</span>}
    </div>
    {!done && !locked && <div className="mt-4">{children}</div>}
  </div>
);

// Phase 2: Live Accident verification gate — safety screening, phone and GPS
// verification must complete before the intake wizard unlocks.
export default function LiveVerificationGate({ caseId, view, onRefresh }) {
  const [safety, setSafety] = useState({ safe_location: null, needs_emergency: null, serious_injury: null, vehicles_involved: 2, police_responded: null, ems_responded: null });
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(null);
  const [emergency, setEmergency] = useState(false);
  const [manualReason, setManualReason] = useState("");
  const [showManual, setShowManual] = useState(false);

  const safetyDone = !!view.safety;
  const phoneDone = view.phone_verified;
  const gpsDone = view.gps_verified;
  const manualPending = view.stages?.find((s) => s.key === "gps")?.manual_review?.status === "pending";

  const submitSafety = async () => {
    const qs = ["safe_location", "needs_emergency", "serious_injury", "police_responded", "ems_responded"];
    if (qs.some((k) => safety[k] === null)) { toast.error("Please answer every question."); return; }
    setBusy("safety");
    try {
      const { data } = await api.post(`/cases/${caseId}/verify/safety`, safety);
      if (data.emergency_guidance) setEmergency(true);
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save"); }
    finally { setBusy(null); }
  };

  const startPhone = async () => {
    if (phone.trim().length < 7) { toast.error("Enter a valid phone number."); return; }
    setBusy("phone");
    try {
      const { data } = await api.post(`/cases/${caseId}/verify/phone/start`, { phone: phone.trim() });
      if (data.already_verified) { toast.success("Phone already verified"); onRefresh(); return; }
      setCodeSent(true);
      if (data.dev_code) setDevCode(data.dev_code);
      toast.success("Verification code sent by SMS");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not send code"); }
    finally { setBusy(null); }
  };

  const confirmPhone = async () => {
    setBusy("code");
    try {
      await api.post(`/cases/${caseId}/verify/phone/confirm`, { code: code.trim() });
      toast.success("Phone verified");
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Incorrect code"); }
    finally { setBusy(null); }
  };

  const shareGps = () => {
    if (!navigator.geolocation) { setShowManual(true); return; }
    setBusy("gps");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.post(`/cases/${caseId}/verify/gps`, { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
          toast.success("Location verified");
          onRefresh();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not verify location"); }
        finally { setBusy(null); }
      },
      () => { setBusy(null); setShowManual(true); },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const requestManual = async () => {
    if (manualReason.trim().length < 5) { toast.error("Briefly explain why GPS is unavailable."); return; }
    setBusy("manual");
    try {
      const { data } = await api.post(`/cases/${caseId}/verify/gps/manual-review`, { reason: manualReason.trim() });
      toast.success(data.message);
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not submit"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-4 cr-fade-up" data-testid="verification-gate">
      <div className="rounded-3xl bg-navy text-white p-6">
        <div className="flex items-center gap-2 text-cyan-300 text-[11px] font-semibold uppercase tracking-[0.15em]"><ShieldCheck className="w-4 h-4" /> Live accident verification</div>
        <h2 className="font-heading text-xl font-extrabold mt-1">A few quick checks before evidence capture</h2>
        <p className="text-sm text-slate-300 mt-1">These steps protect your case and unlock the rest of your report. Verification never delays emergency help — if anyone is in danger, call 911 first.</p>
      </div>

      {emergency && (
        <div className="rounded-3xl bg-red-50 border border-red-200 p-5 flex items-start gap-3" data-testid="emergency-guidance">
          <Siren className="w-6 h-6 text-red-600 shrink-0" />
          <div>
            <p className="font-semibold text-red-700">If anyone needs emergency assistance or is seriously injured, call 911 now.</p>
            <a href="tel:911" className="mt-2 inline-flex items-center gap-2 bg-red-600 text-white text-sm font-bold px-5 py-2.5 rounded-full" data-testid="call-911-btn"><Phone className="w-4 h-4" /> Call 911</a>
          </div>
        </div>
      )}

      <Section done={safetyDone} icon={ShieldCheck} title="1. Safety screening">
        <YesNo k="safe_location" label="Are you in a safe location?" testid="safety-safe" safety={safety} setSafety={setSafety} />
        <YesNo k="needs_emergency" label="Does anyone need emergency assistance?" testid="safety-emergency" safety={safety} setSafety={setSafety} />
        <YesNo k="serious_injury" label="Is anyone seriously injured?" testid="safety-injury" safety={safety} setSafety={setSafety} />
        <div className="flex items-center justify-between gap-3 py-2.5 border-b border-slate-100">
          <span className="text-sm text-slate-700">How many vehicles were involved?</span>
          <input type="number" inputMode="numeric" min={1} max={20} value={safety.vehicles_involved} data-testid="safety-vehicles"
            onChange={(e) => setSafety((s) => ({ ...s, vehicles_involved: Number(e.target.value) || 1 }))}
            className="w-20 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-sm outline-none focus:border-cobalt" />
        </div>
        <YesNo k="police_responded" label="Did police respond?" testid="safety-police" safety={safety} setSafety={setSafety} />
        <YesNo k="ems_responded" label="Did EMS respond?" testid="safety-ems" safety={safety} setSafety={setSafety} />
        <button onClick={submitSafety} disabled={busy === "safety"} data-testid="safety-submit"
          className="mt-4 w-full bg-navy text-white font-semibold py-3 rounded-full hover:opacity-90 transition-all disabled:opacity-50">
          {busy === "safety" ? "Saving…" : "Continue"}
        </button>
      </Section>

      <Section done={phoneDone} locked={!safetyDone} icon={Phone} title="2. Phone verification">
        {!codeSent ? (
          <div className="flex gap-2">
            <input type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="Mobile number (e.g. +1 555 123 4567)" data-testid="phone-input" className={inputCls} />
            <button onClick={startPhone} disabled={busy === "phone"} data-testid="phone-send-btn"
              className="shrink-0 px-5 py-2.5 rounded-xl bg-navy text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
              {busy === "phone" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send code"}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {devCode && <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-xs text-amber-700" data-testid="dev-code-hint">SMS provider not yet configured — your code is <b>{devCode}</b></div>}
            <div className="flex gap-2">
              <input type="text" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)}
                placeholder="6-digit code" maxLength={6} data-testid="phone-code-input" className={`${inputCls} tracking-[0.3em] font-bold`} />
              <button onClick={confirmPhone} disabled={busy === "code"} data-testid="phone-confirm-btn"
                className="shrink-0 px-5 py-2.5 rounded-xl bg-navy text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
                {busy === "code" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
              </button>
            </div>
            <button onClick={startPhone} className="text-xs text-cobalt font-medium">Resend code</button>
          </div>
        )}
      </Section>

      <Section done={gpsDone} locked={!safetyDone || !phoneDone} icon={MapPin} title="3. Location & timestamp verification">
        {manualPending ? (
          <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-sm text-amber-700" data-testid="gps-manual-pending">
            Your manual location review is pending. You may continue documenting, but no professionals will be notified until it is approved.
          </div>
        ) : !showManual ? (
          <>
            <p className="text-sm text-slate-500">We capture your current GPS coordinates and session timestamp to confirm you're near the accident location.</p>
            <button onClick={shareGps} disabled={busy === "gps"} data-testid="gps-share-btn"
              className="mt-3 w-full flex items-center justify-center gap-2 bg-navy text-white font-semibold py-3 rounded-full hover:opacity-90 disabled:opacity-50">
              {busy === "gps" ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />} Share my location
            </button>
            <button onClick={() => setShowManual(true)} className="mt-2 w-full text-xs text-slate-400 hover:text-cobalt" data-testid="gps-manual-link">GPS unavailable? Request manual review</button>
          </>
        ) : (
          <div className="space-y-3" data-testid="gps-manual-form">
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-xs text-amber-700 flex items-start gap-2">
              <MessageSquareWarning className="w-4 h-4 shrink-0 mt-0.5" /> Location verification is required before continuing with a live accident report. If GPS is unavailable, request a manual review — no professionals are notified automatically.
            </div>
            <textarea value={manualReason} onChange={(e) => setManualReason(e.target.value)} data-testid="gps-manual-reason"
              placeholder="Why is GPS unavailable? (e.g. parking garage, device without GPS)" className={`${inputCls} min-h-[70px]`} />
            <div className="flex gap-2">
              <button onClick={requestManual} disabled={busy === "manual"} data-testid="gps-manual-submit"
                className="flex-1 bg-navy text-white text-sm font-semibold py-2.5 rounded-full hover:opacity-90 disabled:opacity-50">Submit for review</button>
              <button onClick={() => setShowManual(false)} className="px-4 text-sm text-slate-500">Back</button>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}
